import { useState, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ArrowLeft, Pencil, Trash2, Building2, Mail, Phone, Globe, MapPin } from "lucide-react"
import { useCompany, useDeleteCompany } from "@/hooks/use-companies"
import { RecordNavigation } from "@/components/record-navigation"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { EntityTabs, type TabConfig } from "@/components/entity-tabs"
import { QuickActions, type QuickActionsEntity } from "@/components/quick-actions"
import { formatDate } from "@/lib/utils"
import { toast } from "sonner"

const COMPANY_TABS: TabConfig[] = [
  { key: "notes", label: "Notatki", countKey: "notes" },
  { key: "activities", label: "Aktywnosci" },
  { key: "contacts", label: "Osoby", countKey: "contacts" },
  { key: "deals", label: "Transakcje", countKey: "deals" },
  { key: "tasks", label: "Zadania", countKey: "tasks" },
  { key: "service_tickets", label: "Serwis", countKey: "service_tickets" },
  { key: "documents", label: "Dokumenty", countKey: "documents" },
  { key: "quotes", label: "Oferty", countKey: "quotes" },
  { key: "invoices", label: "Faktury", countKey: "invoices" },
]

export function CompanyDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useCompany(id)
  const deleteMutation = useDeleteCompany()
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined)
  const tabsRef = useRef<HTMLDivElement>(null)

  const company = data?.data

  const handleOpenNotes = useCallback(() => {
    setActiveTab("notes")
    // Scroll to tabs section after a brief delay for the tab switch to render
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 50)
  }, [])

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab)
  }, [])

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac tego kontrahenta?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Kontrahent usuniety")
    navigate("/companies")
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!company) {
    return <div className="text-muted-foreground">Nie znaleziono kontrahenta</div>
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t("companies.title"), href: "/companies" },
          { label: company.name },
        ]}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/companies")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <RecordNavigation entityType="companies" currentId={id} basePath="/companies" />
          <div>
            <div className="flex items-center gap-2">
              <Building2 size={20} className="text-primary" />
              <h1 className="page-title">
                {company.name}
              </h1>
            </div>
            {company.short_name && (
              <p className="ml-7 text-sm text-muted-foreground">{company.short_name}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {company && id && (
            <QuickActions
              entity={{
                id,
                entityType: "Company",
                name: company.name,
                primaryEmail: company.emails.find((e) => e.is_primary)?.email ?? company.emails[0]?.email ?? null,
                primaryPhone: company.phones.find((p) => p.is_primary)?.number ?? company.phones[0]?.number ?? null,
              } satisfies QuickActionsEntity}
              onOpenNotes={handleOpenNotes}
            />
          )}
          <div className="mx-1 hidden md:block h-5 w-px bg-border" />
          <button onClick={() => navigate(`/companies/${id}/edit`)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input px-3 text-sm transition-colors text-foreground">
            <Pencil size={14} /> {t("common.edit")}
          </button>
          <button onClick={handleDelete}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm transition-colors text-error">
            <Trash2 size={14} /> {t("common.delete")}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Main content */}
        <div className="col-span-2 space-y-6">
          {/* IDs */}
          <section className="surface-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Dane rejestrowe</h2>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">NIP</span>
                <p className="font-mono text-foreground">{company.nip || "\u2014"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">REGON</span>
                <p className="font-mono text-foreground">{company.regon || "\u2014"}</p>
              </div>
              <div>
                <span className="text-muted-foreground">KRS</span>
                <p className="font-mono text-foreground">{company.krs || "\u2014"}</p>
              </div>
            </div>
          </section>

          {/* Contact info */}
          <section className="surface-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Dane kontaktowe</h2>
            <div className="space-y-2">
              {company.emails.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-sm">
                  <Mail size={14} className="text-muted-foreground" />
                  <a href={`mailto:${e.email}`} className="hover:underline text-primary">{e.email}</a>
                  {e.is_primary && <Badge variant="outline" className="text-[10px]">Glowny</Badge>}
                  {e.label && <span className="text-xs text-muted-foreground">({e.label})</span>}
                </div>
              ))}
              {company.phones.map((p) => (
                <div key={p.id} className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-muted-foreground" />
                  <a href={`tel:${p.number}`} className="hover:underline text-primary">{p.number}</a>
                  {p.is_primary && <Badge variant="outline" className="text-[10px]">Glowny</Badge>}
                  {p.label && <span className="text-xs text-muted-foreground">({p.label})</span>}
                </div>
              ))}
              {company.website && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe size={14} className="text-muted-foreground" />
                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="hover:underline text-primary">{company.website}</a>
                </div>
              )}
              {(company.street || company.city) && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-muted-foreground" />
                  <span className="text-foreground">
                    {[company.street, company.postal_code, company.city, company.country].filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Description */}
          {company.description && (
            <section className="surface-card p-4 space-y-2">
              <h2 className="text-sm font-semibold text-foreground">{t("companies.description")}</h2>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{company.description}</p>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="surface-card p-4 space-y-3">
            {company.status && (
              <div>
                <span className="text-xs text-muted-foreground">{t("companies.status")}</span>
                <p><Badge variant="outline">{company.status.label}</Badge></p>
              </div>
            )}
            {company.industry && (
              <div>
                <span className="text-xs text-muted-foreground">{t("companies.industry")}</span>
                <p className="text-sm text-foreground">{company.industry.label}</p>
              </div>
            )}
            {company.owner && (
              <div>
                <span className="text-xs text-muted-foreground">{t("companies.owner")}</span>
                <p className="text-sm text-foreground">{company.owner.first_name} {company.owner.last_name}</p>
              </div>
            )}
            {company.acquisition_source && (
              <div>
                <span className="text-xs text-muted-foreground">Zrodlo</span>
                <p className="text-sm text-foreground">{company.acquisition_source.label}</p>
              </div>
            )}
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">{t("companies.created")}</span>
              <p className="text-sm text-foreground">{formatDate(company.created_at)}</p>
            </div>
            {company.tags.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Tagi</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {company.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Entity Tabs */}
      {id && (
        <div ref={tabsRef}>
          <EntityTabs
            entityType="Company"
            entityId={id}
            tabs={COMPANY_TABS}
            defaultTab="notes"
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>
      )}
    </div>
  )
}
